import React, { useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Add, ArrowBack, Delete, Edit } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { User } from '../../types';
import { deleteChild, useFamily } from './services';

const FamilyManagement: React.FC = () => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const family = useFamily(userData?.familyId || '');
  const children = family.filter((m) => m.userType === 'child') as User[];

  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteChild(deleteTarget.userId);
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4, mb: 6 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <IconButton onClick={() => navigate('/checkin')}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h6" sx={{ flex: 1 }}>
          Manage Family
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<Add />}
          onClick={() => navigate('/checkin/family/add')}
        >
          Add Child
        </Button>
      </Stack>

      {children.length === 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="center"
          sx={{ mt: 6 }}
        >
          No children added yet. Tap &quot;Add Child&quot; to get started.
        </Typography>
      )}

      {children.map((child) => (
        <Box
          key={child.userId}
          sx={{ border: '1px solid #ccc', borderRadius: 2, p: 1.5, mb: 2 }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar
              src={child.profilePic ?? undefined}
              alt={child.firstName}
              sx={{ width: 56, height: 56 }}
            >
              {child.firstName?.[0]}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" fontWeight="bold">
                {child.firstName} {child.lastName}
              </Typography>
              {child.dateOfBirth && (
                <Typography variant="body2" color="text.secondary">
                  DOB:{' '}
                  {child.dateOfBirth.toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </Typography>
              )}
              {child.allergies && child.allergies !== 'N/A' && (
                <Typography variant="body2" color="error">
                  ⚠ {child.allergies}
                </Typography>
              )}
            </Box>
            <Stack direction="row">
              <IconButton
                onClick={() => navigate(`/checkin/family/${child.userId}`)}
              >
                <Edit />
              </IconButton>
              <IconButton color="error" onClick={() => setDeleteTarget(child)}>
                <Delete />
              </IconButton>
            </Stack>
          </Stack>
        </Box>
      ))}

      <Dialog
        open={!!deleteTarget}
        onClose={() => !deleting && setDeleteTarget(null)}
      >
        <DialogTitle>Remove Child</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove{' '}
            <strong>
              {deleteTarget?.firstName} {deleteTarget?.lastName}
            </strong>{' '}
            from your family? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : undefined}
          >
            {deleting ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FamilyManagement;
